'use client'

import { useState } from 'react'
import type { CompanyProfileData } from '@/types/api.types'
import { EditCompanyModal } from './edit-company-modal'
import { VerificationBadge } from './verification-badge'

interface CompanyHeaderProps {
  company: CompanyProfileData
}

export function CompanyHeader({ company }: CompanyHeaderProps) {
  const [showEditModal, setShowEditModal] = useState(false)
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }

  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="flex items-start gap-4">
        {/* Logo */}
        {company.logoUrl ? (
          <img
            src={company.logoUrl}
            alt={`${company.name} logo`}
            className="h-20 w-20 rounded-xl object-cover"
          />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-[var(--color-bg-elevated)] text-2xl font-bold text-[var(--color-text-muted)]">
            {company.name.charAt(0).toUpperCase()}
          </div>
        )}

        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]" style={font}>
              {company.name}
            </h1>

            {/* Verification level badge */}
            <VerificationBadge level={company.verificationLevel} size="md" />

            {/* Status badge */}
            {company.verifiedBadge && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                aria-label="Company status: Verified"
              >
                &#10003; Verified
              </span>
            )}
            {company.isOwner && company.status === 'rejected' && (
              <span
                className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800"
                aria-label="Company status: Rejected"
              >
                Rejected
              </span>
            )}
            {company.isOwner && company.status === 'suspended' && (
              <span
                className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800"
                aria-label="Company status: Suspended"
              >
                Suspended
              </span>
            )}
          </div>

          <p className="text-sm text-[var(--color-text-secondary)]" style={font}>
            {company.industry} &middot; {company.size} employees
            {company.stats.activeJobs > 0 && (
              <> &middot; {company.stats.activeJobs} open {company.stats.activeJobs === 1 ? 'position' : 'positions'}</>
            )}
          </p>

          <p className="text-xs text-[var(--color-text-muted)]" style={font}>
            Member since {company.stats.memberSince}
          </p>
        </div>

        {/* Edit button (owner only) */}
        {company.isOwner && (
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="rounded-[var(--radius-md)] border border-[var(--color-text-muted)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)]"
            style={font}
          >
            Edit profile
          </button>
        )}
      </div>

      {/* Rejected banner (owner only) */}
      {company.isOwner && company.status === 'rejected' && (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 p-4">
          <p className="text-sm text-[var(--color-error)]" style={font}>
            Your company application was not approved. Please update your details and re-submit.
          </p>
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="mt-2 text-sm font-medium text-[var(--color-error)] underline hover:no-underline"
            style={font}
          >
            Update &amp; resubmit
          </button>
        </div>
      )}

      {/* Suspended notice (owner only) */}
      {company.isOwner && company.status === 'suspended' && (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 p-4">
          <p className="text-sm text-[var(--color-error)]" style={font}>
            Your company account has been suspended. If you believe this is an error, please contact{' '}
            <a href="mailto:support@promptvan.com" className="underline">support@promptvan.com</a>.
          </p>
        </div>
      )}

      {showEditModal && (
        <EditCompanyModal company={company} onClose={() => setShowEditModal(false)} />
      )}
    </div>
  )
}
